/**
 * React Query hooks for the property-scoped inbox tab
 *
 * @module hooks/usePropertyInbox
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InboxRecipient } from './useInboxRecipients';
import type { InboxMessageListItem, InboxMessageDetailResponse } from '../ui/components/inbox/types';
import { toApiError } from '../lib/api-error';
import { API_URL } from '../config';

type InboxMessagesResponse = {
  ok: boolean;
  data: InboxMessageListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export function usePropertyInboxRecipients(propertyId: string) {
  return useQuery({
    queryKey: ['property-inbox-recipients', propertyId],
    queryFn: async (): Promise<InboxRecipient[]> => {
      const res = await fetch(`${API_URL}/email/recipients?propertyId=${propertyId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch inbox recipients');
      const data = await res.json();
      return data.recipients;
    },
  });
}

export function usePropertyInboxMessages(params: {
  propertyId: string;
  recipientId?: string | null;
  search?: string | null;
  page?: number;
  pageSize?: number;
}) {
  const { propertyId, recipientId, search, page = 1, pageSize = 25 } = params;

  return useQuery({
    queryKey: ['property-inbox-messages', { propertyId, recipientId, search, page, pageSize }],
    queryFn: async (): Promise<InboxMessagesResponse> => {
      const sp = new URLSearchParams();
      sp.set('propertyId', propertyId);
      sp.set('page', String(page));
      sp.set('pageSize', String(pageSize));
      if (recipientId) sp.set('recipientId', recipientId);
      if (search) sp.set('search', search);

      const res = await fetch(`${API_URL}/email/inbox?${sp.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch inbox messages');
      return res.json();
    },
  });
}

export function useDeleteInboxMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/email/inbox/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to delete message');
      return res.json();
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['property-inbox-messages'] });
      queryClient.removeQueries({ queryKey: ['property-inbox-message-detail', id] });
    },
  });
}

export function usePropertyInboxMessageDetail(messageId: string | null) {
  return useQuery({
    queryKey: ['property-inbox-message-detail', messageId],
    queryFn: async (): Promise<InboxMessageDetailResponse> => {
      const res = await fetch(`${API_URL}/email/inbox/${messageId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch message detail');
      return res.json();
    },
    enabled: Boolean(messageId),
  });
}
